import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Check, Trash2, Plus, Sparkles, Wrench, RefreshCw, ExternalLink, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Side drawer inside the estimate editor showing a tool checklist linked
 * to the current estimate. Features:
 *  - Generate (or regenerate-with-merge) via Claude
 *  - Pack-state persisted server-side per estimate
 *  - Inline add/remove/edit tool names
 *  - Open in Tools Library link (navigates to /tools)
 *  - Close button
 */
export default function ToolsDrawer({ estimateId, onClose, onNavigateToTools }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const packDebounce = useRef(null);

  // Fetch existing checklist on mount
  useEffect(() => {
    if (!estimateId) return;
    setLoading(true);
    fetch(`${API}/estimates/${estimateId}/tools`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(c => {
        setChecklist(c);
        setShowConfirm(!c);  // If nothing exists yet, show the confirm CTA
      })
      .catch(() => setShowConfirm(true))
      .finally(() => setLoading(false));
  }, [estimateId]);

  const generateOrRegenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/estimates/${estimateId}/tools/generate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'AI failed');
        return;
      }
      const data = await res.json();
      const stats = data._merge_stats || {};
      setChecklist(data);
      setShowConfirm(false);
      if (stats.existing_count > 0) {
        toast.success(
          stats.added > 0
            ? `${stats.added} new tool${stats.added !== 1 ? 's' : ''} added · ${stats.packed_preserved} packed preserved`
            : 'No new tools — list already up to date'
        );
      } else {
        toast.success(`Generated ${stats.total_count} tools from this estimate`);
      }
    } catch { toast.error('Request failed'); }
    finally { setGenerating(false); }
  };

  const updateField = async (patch) => {
    if (!checklist) return;
    setChecklist(prev => ({ ...prev, ...patch }));  // optimistic
    try {
      await fetch(`${API}/tools/${checklist.checklist_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
    } catch { /* ignore */ }
  };

  const commitTools = async () => {
    if (!checklist) return;
    try {
      await fetch(`${API}/tools/${checklist.checklist_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tools: checklist.tools }),
      });
    } catch { /* ignore */ }
  };

  const addTool = () => {
    if (!checklist) return;
    setChecklist(prev => ({ ...prev, tools: [...(prev.tools || []), ''] }));
  };

  const removeTool = async (idx) => {
    if (!checklist) return;
    const nextTools = checklist.tools.filter((_, i) => i !== idx);
    // Shift packed indexes
    const nextPacked = (checklist.packed || [])
      .filter(p => p !== idx)
      .map(p => (p > idx ? p - 1 : p));
    setChecklist(prev => ({ ...prev, tools: nextTools, packed: nextPacked }));
    try {
      await fetch(`${API}/tools/${checklist.checklist_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tools: nextTools }),
      });
      savePackState(nextPacked);
    } catch { /* ignore */ }
  };

  const updateTool = (idx, value) => {
    if (!checklist) return;
    const next = [...checklist.tools];
    next[idx] = value;
    setChecklist(prev => ({ ...prev, tools: next }));
  };

  const savePackState = useCallback((packed) => {
    if (!checklist) return;
    if (packDebounce.current) clearTimeout(packDebounce.current);
    packDebounce.current = setTimeout(() => {
      fetch(`${API}/tools/${checklist.checklist_id}/pack`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packed }),
      }).catch(() => {});
    }, 400);
  }, [checklist]);

  const togglePacked = (idx) => {
    if (!checklist) return;
    const set = new Set(checklist.packed || []);
    if (set.has(idx)) set.delete(idx); else set.add(idx);
    const next = Array.from(set).sort((a, b) => a - b);
    setChecklist(prev => ({ ...prev, packed: next }));
    savePackState(next);
  };

  const resetPacked = () => {
    if (!checklist) return;
    setChecklist(prev => ({ ...prev, packed: [] }));
    savePackState([]);
  };

  const copyList = async () => {
    if (!checklist) return;
    const txt = [
      `${checklist.job_type} — packing list`,
      '',
      ...checklist.tools.map((t, i) => `${(checklist.packed || []).includes(i) ? '[x]' : '[ ]'} ${t}`),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copied to clipboard');
    } catch { toast.error('Clipboard unavailable'); }
  };

  const packedCount = new Set(checklist?.packed || []).size;
  const totalCount = checklist?.tools?.length || 0;

  return (
    <div
      className="bg-white border border-[#E6EBE8] rounded-md h-full flex flex-col overflow-hidden"
      data-testid="tools-drawer"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E6EBE8] flex items-center justify-between flex-shrink-0 bg-[#F9FAFA]">
        <div className="flex items-center gap-2 min-w-0">
          <Wrench className="w-4 h-4 text-[#2D6B68] flex-shrink-0" />
          <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">
            Tools Needed
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-[#6B7A73] hover:bg-[#F0F3F1] hover:text-[#1A1D1C]"
          data-testid="close-tools-drawer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : showConfirm ? (
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#E5F0EE] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#2D6B68]" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                  Generate from estimate
                </h4>
                <p className="text-sm text-[#6B7A73] mt-1 leading-relaxed">
                  Claude will read your section titles, item descriptions, and notes — then suggest a packing checklist of tools, blades, fasteners and safety gear you'll need.
                </p>
              </div>
            </div>
            <Button
              onClick={generateOrRegenerate}
              disabled={generating}
              className="w-full bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md"
              data-testid="generate-tools-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? 'Generating…' : 'Generate checklist'}
            </Button>
            <p className="text-xs text-[#6B7A73] italic">
              You can edit, add, or remove tools afterwards. Pack-mode checkboxes are saved so they persist between visits.
            </p>
          </div>
        ) : checklist ? (
          <div className="p-4 space-y-4">
            <div>
              <Input
                value={checklist.job_type}
                onChange={e => setChecklist(prev => ({ ...prev, job_type: e.target.value }))}
                onBlur={() => updateField({ job_type: checklist.job_type })}
                className="text-sm font-medium border-[#E6EBE8]"
                data-testid="checklist-title"
              />
              {checklist.description && (
                <p className="text-[11px] text-[#6B7A73] mt-1 leading-relaxed">{checklist.description}</p>
              )}
            </div>

            {totalCount > 0 && (
              <div className="flex items-center gap-2 bg-[#F9F9F8] border border-[#E6EBE8] rounded-md px-3 py-2">
                <div className="flex-1">
                  <div className="h-1.5 bg-[#E6EBE8] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#2D6B68] transition-all"
                      style={{ width: `${(packedCount / totalCount) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="text-[11px] text-[#6B7A73] font-medium tabular-nums whitespace-nowrap"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {packedCount} / {totalCount}
                </span>
                {packedCount > 0 && (
                  <button
                    onClick={resetPacked}
                    className="text-[10px] uppercase tracking-wider text-[#6B7A73] hover:text-[#1A1D1C]"
                    data-testid="reset-pack"
                  >
                    Reset
                  </button>
                )}
              </div>
            )}

            <div className="space-y-1">
              {(checklist.tools || []).map((tool, idx) => {
                const packed = (checklist.packed || []).includes(idx);
                return (
                  <div key={idx} className="flex items-center gap-2 group" data-testid={`drawer-tool-${idx}`}>
                    <button
                      onClick={() => togglePacked(idx)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        packed
                          ? 'bg-[#2D6B68] border-[#2D6B68] text-white'
                          : 'bg-white border-[#E6EBE8] hover:border-[#2D6B68]'
                      }`}
                      data-testid={`drawer-check-${idx}`}
                    >
                      {packed && <Check className="w-3 h-3" />}
                    </button>
                    <Input
                      value={tool}
                      onChange={e => updateTool(idx, e.target.value)}
                      onBlur={commitTools}
                      className={`flex-1 h-8 text-sm border-[#E6EBE8] bg-white ${
                        packed ? 'line-through text-[#6B7A73]' : 'text-[#1A1D1C]'
                      }`}
                    />
                    <button
                      onClick={() => removeTool(idx)}
                      className="p-1 text-[#6B7A73] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              <Button
                onClick={addTool}
                variant="ghost"
                size="sm"
                className="text-[#2D6B68] hover:bg-[#F0F3F1] mt-1"
                data-testid="drawer-add-tool"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add tool
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      {checklist && !showConfirm && (
        <div className="px-4 py-3 border-t border-[#E6EBE8] bg-[#F9FAFA] flex items-center gap-2 flex-wrap">
          <Button
            onClick={generateOrRegenerate}
            disabled={generating}
            variant="outline"
            size="sm"
            className="border-[#E6EBE8] text-[#2D6B68] hover:bg-[#F0F3F1]"
            data-testid="regenerate-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Regenerating…' : 'Regenerate'}
          </Button>
          <Button
            onClick={copyList}
            variant="outline"
            size="sm"
            className="border-[#E6EBE8]"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
          <div className="flex-1" />
          <Button
            onClick={onNavigateToTools}
            variant="ghost"
            size="sm"
            className="text-[#2D6B68] hover:bg-[#F0F3F1]"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Library
          </Button>
        </div>
      )}
    </div>
  );
}
